/**
 * Chapter 1 — "The People Who Make It Happen." V16.
 *
 * One unified scene engine with a shared entity pool. Beats are layout
 * functions that assign new target positions to the same entities —
 * so transitions between beats are continuous morphs of the same
 * physical objects, not cross-fades of different scenes.
 *
 * Text lives in a dedicated lower-third band with a dark gradient
 * underlay so it never competes with the animation. The scene camera
 * is shifted upward (~40% viewport height) so the visual happens
 * in the top two-thirds while text has the bottom zone to itself.
 */
import {
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from "react";
import { createAudioEngine, type AudioEngineHandle } from "./AudioEngine";
import { BlueprintGrid } from "./BlueprintGrid";
import { BrandReveal } from "./BrandReveal";
import { Chapter1Engine } from "./Chapter1Engine";
import { LAYOUTS } from "./Chapter1Layouts";
import {
  ALPHA,
  CREAM_RGB,
  cream,
  LABEL,
} from "./designTokens";
import { TapToStart } from "./TapToStart";

const FONT_HREF =
  "https://fonts.googleapis.com/css2?family=IBM+Plex+Mono:wght@400;500;600&family=Inter:wght@200;300;400&display=swap";

const MIN_BEAT_MS = 1300;
const TEXT_FADE_OUT_MS = 220;

// ──────────────────────────────────────────────────────────────────────
// Beats — each one is text + duration; the visual is computed by
// running the matching layout against the shared entity pool.
//
// v13 "The Investigation" — Johnny Harris-voice monologue that walks
// the viewer from recognition (we're the most connected generation and
// the loneliest) through the question (what if we've never been able
// to see relationships themselves?) to the insight (relationships are
// physics) and finally the tool (Suron reads the signals).
// ──────────────────────────────────────────────────────────────────────

interface Beat {
  id: number;
  text?: string;
  duration: number;
}

// Beat durations are aligned to the generated voice-over clip lengths
// (resources/audio/voice/beat-NN.mp3) + ~600ms breath, or clamped up
// when the animation itself requires more time (path sequence, radar
// boot, brand reveal).
const BEATS: Beat[] = [
  // ── Act I · The Observation ────────────────────────────────────────
  // The opening sentence spans beats 1–2. Comma on beat 1 keeps the
  // thought open so beat 2 lands as the resolution while Earth reveals.
  // Tight ~450ms breath so the piece opens with momentum, not drag.
  { id: 1, text: "for the first time in human history,", duration: 2850 },
  { id: 2, text: "we've never been more connected.", duration: 3050 },
  { id: 3, text: "we can reach anyone on earth in a second.", duration: 3550 },

  // ── Act II · The Paradox ──────────────────────────────────────────
  // "and yet" is THE pivot. Extra 880ms breath after "alone" so the
  // paradox hangs in the air before "loneliness" lands. Statistics
  // that follow are clipped and relentless — no contemplation.
  // beat-03 clip ends at 2.49s (trimmed ghost audio after natural
  // speech end). 910ms breath after "alone" hangs the pivot before
  // "loneliness" lands.
  { id: 4, text: "and yet, we've never been more alone.", duration: 3400 },
  { id: 5, text: "loneliness is at the highest level ever recorded.", duration: 4150 },
  // Three emotional beats that build the isolation inside the "most
  // connected generation." Each is a specific flavor of wave-physics
  // failure: signal-without-receiver, presence-without-signal,
  // destructive-interference. The parallel "a ___ full of ___"
  // rhythm pulls the viewer through the collapse. "almost" is the
  // word that hits hardest — durations sized so "almost" gets ~700ms
  // to hang in the air before the beat cuts to "silence."
  { id: 6, text: "a phone full of names.",        duration: 2600 },
  { id: 7, text: "a room full of strangers.",     duration: 2550 },
  { id: 8, text: "a life full of... almost.",     duration: 3550 },
  // Silence is the bottom of the arc. Ellipses signal the TTS to slow
  // down and hush toward "silence" — the word itself should land as
  // a near-whisper. Voice slot sized for ~2s of dead air after, so
  // the void IS the point.
  { id: 9, text: "and still... most nights... silence...", duration: 5800 },

  // ── Act III · The Question ────────────────────────────────────────
  // The perspective flip. One thought split across two beats so the
  // rhythm lands as a storyteller's pair, not two statements. Beat 11
  // plants the universal stakes ("everything that matters"); beat 12
  // delivers the Suron thesis in five words ("where you can't see it")
  // — leading straight into "every moment between two people leaves a
  // trace" without needing a bridge.
  { id: 10, text: "so we started asking.", duration: 2100 },
  // Tight ~200ms gap on beat 11 so "what if everything that matters,"
  // bleeds directly into "is happening where you can't see it?" as one
  // continuous storyteller thought — not two stitched recordings. The
  // question mark then hangs for ~500ms before the answer ("every
  // moment between two people leaves a trace") lands.
  { id: 11, text: "what if everything that matters,", duration: 2100 },
  { id: 12, text: "is happening where you can't see it?", duration: 3000 },
  { id: 13, text: "every moment between two people leaves a trace.", duration: 4500 },
  { id: 14, text: "the drift.", duration: 1500 },
  { id: 15, text: "the repair.", duration: 1650 },
  { id: 16, text: "the quiet moments that decide everything.", duration: 3300 },

  // ── Act IV · The Insight ──────────────────────────────────────────
  // Aha moment — 940ms breath after "physics" so the revelation lands
  // in silence before the emotion demos open.
  { id: 17, text: "relationships are physics.", duration: 3050 },

  // Emotion demos — voice is quick; duration is sized so the wave
  // visualization has ~1s to bloom and settle after the word.
  { id: 18, text: "but so are your emotions.", duration: 2700 },
  { id: 19, text: "happiness.", duration: 1800 },
  { id: 20, text: "sadness.", duration: 2000 },
  { id: 21, text: "anxiety.", duration: 2100 },
  { id: 22, text: "anger.", duration: 1750 },
  { id: 23, text: "tranquility.", duration: 2400 },
  { id: 24, text: "now put two together.", duration: 2200 },

  // Pair physics — each demo needs time for the wave interference
  // pattern to land on the mesh. Love + fight get the biggest bumps:
  // love's hypnotic unified swell and fight's dissonant clash both
  // need breathing room to read as the physics they are.
  { id: 25, text: "this is friendship.", duration: 2400 },
  { id: 26, text: "this is love.", duration: 2800 },
  { id: 27, text: "this is a fight.", duration: 2700 },
  { id: 28, text: "this is growing apart.", duration: 2700 },

  { id: 29, text: "we've been walking through them half-blind.", duration: 3150 },

  // ── Act V · The Timing ────────────────────────────────────────────
  { id: 30, text: "for the first time, the invisible becomes visible.", duration: 4100 },
  { id: 32, text: "not the posts. not the clicks.", duration: 3700 },

  // ── Act VI · The Tool ─────────────────────────────────────────────
  { id: 33, text: "that's what we built.", duration: 1950 },
  { id: 35, text: "shows you what's drifting.", duration: 2800 },
  { id: 36, text: "what's growing.", duration: 2500 },
  { id: 37, text: "and what to do next.", duration: 2950 }, // animation-bound: 3 handoffs × 0.9s = 2.7s + 250ms tail
  { id: 38, text: "so you can actually hold the people who matter.", duration: 3450 },

  // ── Act VII · The Promise ─────────────────────────────────────────
  // Motivational climb. "the job / the love / the life you want" each
  // get a firm ~830ms tail so they punch individually before the next
  // hits. Then reflective pullback on "it lives inside someone else."
  { id: 39, text: "every good thing in your life.", duration: 2800 },
  { id: 40, text: "the job.",                       duration: 1550 },
  { id: 41, text: "the love.",                      duration: 1550 },
  { id: 42, text: "the life you want.",             duration: 2100 },
  // Reflective pull-back after the "life you want" peak. Slowed from
  // 3150 → 3600 so Tess's settling voice (speed 0.78) has room to
  // breathe and the viewer feels the release from the staircase.
  { id: 43, text: "it lives inside someone else.",  duration: 3600 },
  // Second-to-last scene. Extended significantly (3050 → 4800) for
  // three reasons, all director-level:
  //   · voice slowed to speed 0.74 — the line clocks ~3.5s spoken
  //   · animation (`beatFound`) retuned to resolve at t=3.6 —
  //     viewer now SEES the hero-ring form instead of cutting at
  //     mid-motion
  //   · last ~1s is pure held silence + settled ring, so the
  //     whispered finale lands as a true exhale, not a cut
  { id: 44, text: "you just need to know how to reach them.", duration: 4800 },

  // ── Finale ─────────────────────────────────────────────────────────
  // Whispered closer mirroring the beat 8 "silence..." template.
  // Ellipsis on "beyond..." forces a ~450ms prosodic pause so the
  // word hangs; trailing "..." tells ElevenLabs to exhale "noise"
  // into decay rather than buttoning it. Cathedral reverb then does
  // the fade work. 9s beat tail holds the wordmark reveal.
  { id: 45, text: "beyond... the noise...", duration: 9000 },
];

/**
 * Per-beat atmospheric SFX cues, keyed to beat-array INDEX (not id).
 * Each plays at a low gain so it colours the scene without competing
 * with the voice or the music bed. Only beats with an unambiguous
 * sonic identity get a cue — the rest ride on music + voice alone.
 * The finale pulsar is a separate heavier hit handled below.
 */
interface SubtleSfx {
  name: string;
  gain: number;
}
/**
 * Per-beat music dynamics — the chapter's emotional rollercoaster
 * mapped to concrete bed gains. Beats not in this map inherit the
 * previous beat's level (Web Audio keeps lerping, no reset).
 *
 * Three ranges are handled separately and DELIBERATELY omitted from
 * this table because they need multi-step or continuous fades that
 * would be broken by per-beat fadeMusicTo calls:
 *   · beats 8 – 17  silence duck + 22s continuous swell to the
 *                   "relationships are physics" peak at beat 16 and
 *                   the relax back down at beat 17
 *   · beats 40 – 42 finale fade (BEATS.length − 3 onwards)
 */
interface MusicCue {
  level: number;
  fadeDur: number;
}
/**
 * Music dynamics — Interstellar-inspired scene-by-scene design.
 *
 * The arc of the whole piece:
 *   - Open in pure silence (beat 0). Just voice. Intimate. The bed
 *     hasn't entered yet. The viewer leans in.
 *   - Gentle swell through observation (beats 1–2). A tide rising.
 *   - ORGAN PEAK on the paradox pivot (beat 3 "alone"). This is the
 *     first big emotional landing.
 *   - Slow deflation through the statistics (beats 4–7) as the
 *     viewer realizes the scale of the problem.
 *   - HARD SILENCE on beat 8 — the point where the whole observation
 *     crystallizes. No bed. Just Tess whispering "silence…"
 *   - Faint ember glowing back on the question (beats 9–11). We're
 *     explaining; music stays under the voice.
 *   - Rising tension through the traces/drift/repair (beats 12–15).
 *   - ORGAN SWELL PEAK on "relationships are physics" (beat 16). The
 *     revelation. Biggest moment of Act IV.
 *   - Bed recedes under emotion demos so each wave reads clearly —
 *     except anger, where we let the bed punch up.
 *   - Pair physics rises on love, snaps on fight, thins on drift.
 *   - REVEAL CREST on "the invisible becomes visible" (beat 29) then
 *     HARD DROP on "not the posts" (beat 30) — the contrast sells
 *     the product moment that follows.
 *   - TOOL CREST on "that's what we built" (beat 31). Second-biggest
 *     peak, matched only by the promise climax.
 *   - Intimacy dip on "hold the people who matter" (beat 35).
 *   - Staircase climb through the promise — "the job / the love /
 *     the life you want" each step up until the absolute peak.
 *   - Finale fade-out is handled inline.
 *
 * Fade durations: longer (2.2–3.0s) on big emotional landings so they
 * feel earned; shorter (0.8–1.2s) for quick reactive shifts (fight,
 * dip under intimacy, hard contrast drops).
 */
const MUSIC_CUES: ReadonlyMap<number, MusicCue> = new Map([
  // ── Act I · Observation ───────────────────────────────────────────
  [0,  { level: 0.00, fadeDur: 0.5 }], // "for the first time in human history," — SILENT. Voice only.
  [1,  { level: 0.12, fadeDur: 2.8 }], // "we've never been more connected." — bed gently enters
  [2,  { level: 0.22, fadeDur: 2.2 }], // "we can reach anyone on earth in a second." — tide rising

  // ── Act II · Paradox ──────────────────────────────────────────────
  // Crescendo crest on "never been more alone" → "loneliness highest ever"
  // (the diagnostic peak), then a SUSTAINED COLLAPSE through the three
  // emotional beats that follow. Music falls out from under the viewer
  // as each "a ___ full of ___" line lands more emptily than the last,
  // terminating in total silence under the word "silence."
  [3,  { level: 0.42, fadeDur: 2.6 }], // "we've never been more alone." — ORGAN APEX
  [4,  { level: 0.44, fadeDur: 1.4 }], // "loneliness is at the highest level ever recorded." — CREST
  [5,  { level: 0.32, fadeDur: 1.4 }], // "a phone full of names." — first drop, gravity starts
  [6,  { level: 0.20, fadeDur: 1.4 }], // "a room full of strangers." — colder, deeper
  [7,  { level: 0.08, fadeDur: 1.8 }], // "a life full of... almost." — near-whisper under "almost"
  // Beat 8 silence — music fades to 0 over most of the beat so the
  // word "silence" lands in a genuine silence, not a fading tail.
  // Keeping 2.2s fade (most of beat 9's 5.4s is true quiet).
  [8,  { level: 0.00, fadeDur: 2.2 }], // "and still... most nights... silence..." — DEAD SILENCE

  // ── Act III · Question ────────────────────────────────────────────
  // Beat 9 "so we started asking" — longer fade-in (2.8s) so the bed
  // rises gradually THROUGHOUT this beat, mirroring the animation's
  // re-ignition from ember to full. No pop from silence — just a
  // slow-blooming warmth returning to the room as the thought forms.
  [9,  { level: 0.08, fadeDur: 2.8 }], // "so we started asking." — slow warm return
  [10, { level: 0.10, fadeDur: 1.2 }], // "what if everything that matters,"
  [11, { level: 0.14, fadeDur: 1.2 }], // "is happening where you can't see it?"
  [12, { level: 0.20, fadeDur: 1.8 }], // "every moment between two people leaves a trace."
  [13, { level: 0.22, fadeDur: 0.8 }], // "the drift." — emphatic
  [14, { level: 0.22, fadeDur: 0.8 }], // "the repair." — emphatic
  [15, { level: 0.32, fadeDur: 2.0 }], // "the quiet moments that decide everything." — rising

  // ── Act IV · Insight + Emotion demos ──────────────────────────────
  [16, { level: 0.48, fadeDur: 2.5 }], // "relationships are physics." — ORGAN SWELL PEAK
  [17, { level: 0.40, fadeDur: 1.8 }], // "but so are your emotions." — sustain
  [18, { level: 0.24, fadeDur: 1.4 }], // "happiness." — drop so wave reads
  [19, { level: 0.18, fadeDur: 1.4 }], // "sadness." — softer
  [20, { level: 0.26, fadeDur: 0.9 }], // "anxiety." — tense
  [21, { level: 0.34, fadeDur: 0.9 }], // "anger." — charged punch
  [22, { level: 0.16, fadeDur: 1.8 }], // "tranquility." — settled

  // ── Pair physics ──────────────────────────────────────────────────
  [23, { level: 0.24, fadeDur: 1.6 }], // "now put two together."
  [24, { level: 0.30, fadeDur: 1.2 }], // "this is friendship." — warm
  [25, { level: 0.38, fadeDur: 1.2 }], // "this is love." — peak of pair arc
  [26, { level: 0.22, fadeDur: 0.8 }], // "this is a fight." — snap
  [27, { level: 0.13, fadeDur: 1.8 }], // "this is growing apart." — thin, lonely
  [28, { level: 0.22, fadeDur: 1.4 }], // "we've been walking through them half-blind."

  // ── Act V · Timing ────────────────────────────────────────────────
  [29, { level: 0.44, fadeDur: 2.4 }], // "for the first time, the invisible becomes visible." — REVEAL CREST
  [30, { level: 0.04, fadeDur: 0.8 }], // "not the posts. not the clicks." — HARD DROP toward silence

  // ── Act VI · Tool ─────────────────────────────────────────────────
  // Product demo is CLINICAL + INTIMATE. The tool speaks for itself —
  // a low bed lives underneath (not dead silent) so momentum holds
  // during the diagnostic. Bed gently rises through the demo so it's
  // ready to swell on beat 35 when human stakes come back.
  [31, { level: 0.08, fadeDur: 1.4 }], // "that's what we built." — low bed enters, humble reveal
  [32, { level: 0.10, fadeDur: 0.8 }], // "shows you what's drifting."
  [33, { level: 0.12, fadeDur: 0.8 }], // "what's growing."
  [34, { level: 0.16, fadeDur: 1.0 }], // "and what to do next." — gentle crescendo toward 35
  [35, { level: 0.28, fadeDur: 1.4 }], // "so you can actually hold the people who matter." — swell for the human moment

  // ── Act VII · Promise ─────────────────────────────────────────────
  [36, { level: 0.26, fadeDur: 1.4 }], // "every good thing in your life." — climb begins
  [37, { level: 0.32, fadeDur: 0.9 }], // "the job."
  [38, { level: 0.38, fadeDur: 0.9 }], // "the love."
  [39, { level: 0.48, fadeDur: 1.0 }], // "the life you want." — ABSOLUTE PEAK

  // ── Finale fade — silence lands BEFORE the closer ─────────────────
  // The peak at beat 39 releases, and the music comes down like an
  // exhale across beats 40–41 so by the time the whispered closer
  // appears on beat 42, the air is already silent. NOT a fade that
  // finishes INTO the closer (which made music bleed over "beyond
  // the noise"). The two-step descent front-loads the drop:
  //   · beat 40 — sharp release from peak to low ember
  //   · beat 41 — continues fading into true silence by the end of
  //     the beat (≥1s of dead air before the closer lands)
  //   · beat 42 — already silent; whisper lives alone
  [40, { level: 0.10, fadeDur: 2.5 }], // "it lives inside someone else." — sharp release
  [41, { level: 0.00, fadeDur: 2.2 }], // "you just need to know how to reach them." — into silence
  [42, { level: 0.00, fadeDur: 0.6 }], // "beyond... the noise..." — PURE SILENCE, whisper alone
]);

// Per-beat dopamine pluck cues — high starlight twinkles, NOT bell
// tones. Notes live in D major pentatonic across octaves 6–7 (1175–
// 2960 Hz) where human hearing is most sensitive — the genuine
// tickle-at-the-top-of-the-ear zone. Pure sine wave + chorus partner
// through echo = "reach for the stars" shimmer.
interface PluckCue {
  note: number; // Hz
  velocity?: number;
  decaySec?: number;
}
const TWINKLE = {
  D6: 1174.66, E6: 1318.51, Fs6: 1479.98, A6: 1760.0, B6: 1975.53,
  D7: 2349.32, E7: 2637.02, Fs7: 2959.96,
};
const PLUCK_CUES: ReadonlyMap<number, PluckCue> = new Map([
  // Act I · The Observation — opening rise into the stars.
  [0, { note: TWINKLE.D6, velocity: 0.55 }],  // "for the first time"
  [1, { note: TWINKLE.Fs6, velocity: 0.6 }],  // "we've never been more connected"
  [2, { note: TWINKLE.A6, velocity: 0.65 }],  // "anyone on earth in a second"

  // Act II · The Paradox — lift on the pivot, gentle descent through stats.
  [3, { note: TWINKLE.B6, velocity: 0.7, decaySec: 1.1 }], // "never been more alone"
  [4, { note: TWINKLE.A6, velocity: 0.45 }],  // "loneliness"
  [5, { note: TWINKLE.Fs6, velocity: 0.4 }],  // "six hundred"
  [6, { note: TWINKLE.E6, velocity: 0.4 }],   // "dozens"
  [7, { note: TWINKLE.D6, velocity: 0.4 }],   // "hundreds of contacts"
  // beat 8 "silence" — NO pluck.

  // Act III · The Question — ember rising into the upper range.
  [9, { note: TWINKLE.D6, velocity: 0.35 }],  // "so we started asking"
  [10, { note: TWINKLE.Fs6, velocity: 0.4 }], // "what if everything that matters"
  [11, { note: TWINKLE.A6, velocity: 0.6, decaySec: 1.0 }], // "where you can't see it?"
  [12, { note: TWINKLE.Fs6, velocity: 0.45 }], // "leaves a trace"
  [13, { note: TWINKLE.E6, velocity: 0.55 }], // "the drift"
  [14, { note: TWINKLE.Fs6, velocity: 0.55 }], // "the repair"
  [15, { note: TWINKLE.B6, velocity: 0.5, decaySec: 1.2 }], // "the quiet moments"

  // Act V · The Timing — high crest + gentle landing.
  [29, { note: TWINKLE.D7, velocity: 0.75, decaySec: 1.3 }], // "invisible becomes visible"
  [30, { note: TWINKLE.D6, velocity: 0.45 }], // "not the posts"

  // Act VI · The Tool — ascending chime through the demo.
  [31, { note: TWINKLE.E6, velocity: 0.5 }],  // "that's what we built"
  [32, { note: TWINKLE.Fs6, velocity: 0.4 }], // "what's drifting"
  [33, { note: TWINKLE.A6, velocity: 0.4 }],  // "what's growing"
  [34, { note: TWINKLE.B6, velocity: 0.5 }],  // "what to do next"

  // Act VII · Promise — staircase to the absolute star.
  [36, { note: TWINKLE.D6, velocity: 0.5 }],  // "every good thing"
  [37, { note: TWINKLE.Fs6, velocity: 0.6 }], // "the job"
  [38, { note: TWINKLE.A6, velocity: 0.65 }], // "the love"
  [39, { note: TWINKLE.D7, velocity: 0.8, decaySec: 1.4 }], // "the life you want" — peak
  [40, { note: TWINKLE.A6, velocity: 0.55, decaySec: 1.2 }], // "it lives inside someone else"
]);

const SUBTLE_SFX: ReadonlyMap<number, SubtleSfx> = new Map([
  [4,  { name: "tension-rise",      gain: 0.09 }], // "loneliness is at the highest level ever recorded."
  [8,  { name: "hollow-wind",       gain: 0.08 }], // "and still, most nights, silence."
  [16, { name: "insight-swell",     gain: 0.11 }], // "relationships are physics."
  [18, { name: "emo-happiness",     gain: 0.10 }], // "happiness."
  [19, { name: "emo-sadness",       gain: 0.09 }], // "sadness."
  [20, { name: "emo-anxiety",       gain: 0.09 }], // "anxiety."
  [21, { name: "emo-anger",         gain: 0.11 }], // "anger."
  [22, { name: "emo-calm",          gain: 0.10 }], // "calm."
  [29, { name: "radar-sweep",       gain: 0.12 }], // "for the first time, the invisible becomes visible."
  [30, { name: "digital-static",    gain: 0.09 }], // "not the posts. not the clicks."
  [31, { name: "dimensional-bloom", gain: 0.08 }], // "that's what we built."
  [40, { name: "inner-bloom",       gain: 0.11 }], // "it lives inside someone else."
]);

// ──────────────────────────────────────────────────────────────────────
// Driver
// ──────────────────────────────────────────────────────────────────────

export function Chapter1() {
  useIbmPlexMono();

  const [started, setStarted] = useState(false);
  const [muted, setMuted] = useState(false);
  const [beatIndex, setBeatIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const [beatStart, setBeatStart] = useState<number>(() => performance.now());
  const [beatTime, setBeatTime] = useState(0);

  // Audio engine — created once, started on first user tap.
  const audioRef = useRef<AudioEngineHandle | null>(null);
  if (audioRef.current === null && typeof window !== "undefined") {
    audioRef.current = createAudioEngine();
  }

  // On beat change, trigger the matching voice-over + scene-specific
  // atmospheric SFX.
  //
  // Music bed handling around the finale:
  //   · Two beats before finale ("the job, the love, the life you
  //     want. it lives inside someone else.") start a long 8s fade so
  //     music is well on its way to silence by the time the finale
  //     line lands. No mid-loop cut, no abrupt drop.
  //   · At the finale beat, pin music to 0 and play the pulsar hit.
  useEffect(() => {
    if (!started) return;
    const a = audioRef.current;
    if (!a) return;
    a.playVoice({ beatIndex });

    // Subtle atmospheric cue (radar sweep, static, dimensional bloom).
    const subtle = SUBTLE_SFX.get(beatIndex);
    if (subtle) a.playSfx(subtle);

    // Dopamine pluck — pentatonic note for this beat, if any.
    const pluck = PLUCK_CUES.get(beatIndex);
    if (pluck) {
      a.triggerPluck({
        freqHz: pluck.note,
        velocity: pluck.velocity,
        decaySec: pluck.decaySec,
      });
    }

    // Per-beat music dynamics — apply the cue table if this beat has
    // one. The silence/physics/finale windows explicitly skip the
    // table because they run continuous multi-beat fades.
    const cue = MUSIC_CUES.get(beatIndex);
    if (cue) a.fadeMusicTo(cue.level, cue.fadeDur);

    // Music dynamics are now fully driven by MUSIC_CUES — every beat
    // in the piece has an explicit level. No more inline auto-swell.
    // Voice ducking on the silence whisper is preserved: Tess's
    // regenerated clip is already hushed, but a slight voice-bus dip
    // helps the word "silence" land as a vanishing breath rather
    // than at full level.
    const SILENCE_IDX = 8;
    if (beatIndex === SILENCE_IDX) {
      a.fadeVoiceTo(0.55, 1.2);
    } else if (beatIndex === SILENCE_IDX + 1) {
      // Restore voice for the question — Tess starts asking again.
      a.fadeVoiceTo(1.0, 1.6);
    }

    // Finale fade is fully owned by MUSIC_CUES [40]/[41]/[42] now —
    // a measured three-step descent so the whispered last line
    // lands in true silence. Only the pulsar punctuation fires here.
    const finaleAt = BEATS.length - 1;
    if (beatIndex === finaleAt) {
      a.playSfx({ name: "pulsar-finale" });
    }
  }, [beatIndex, started]);

  // Clean up the AudioContext on unmount.
  useEffect(() => {
    return () => {
      audioRef.current?.dispose();
    };
  }, []);

  const handleStart = async (): Promise<void> => {
    await audioRef.current?.start();
    setStarted(true);
    setBeatStart(performance.now());
    setBeatTime(0);
  };

  const handleToggleMute = (): void => {
    const next = !muted;
    setMuted(next);
    audioRef.current?.setMuted(next);
  };

  // Drive auto-advance. Holds at the final beat (the sticky brand
  // reveal). Waits for the tap-to-start gesture before advancing.
  useEffect(() => {
    if (!started) return;
    if (paused) return;
    if (beatIndex >= BEATS.length - 1) return;
    const duration = Math.max(MIN_BEAT_MS, BEATS[beatIndex].duration);
    const t = window.setTimeout(
      () => setBeatIndex((i) => i + 1),
      duration
    );
    return () => window.clearTimeout(t);
  }, [beatIndex, paused, started]);

  // Reset beatTime when beat changes
  useEffect(() => {
    setBeatStart(performance.now());
    setBeatTime(0);
  }, [beatIndex]);

  // Drive beatTime with rAF so layouts can animate internally.
  // Holds at t=0 until the viewer taps to begin.
  useEffect(() => {
    if (!started || paused) return;
    let raf = 0;
    const tick = (time: number): void => {
      setBeatTime((time - beatStart) / 1000);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [beatStart, paused, started]);

  const beat =
    beatIndex < BEATS.length ? BEATS[beatIndex] : BEATS[BEATS.length - 1];
  const layout = LAYOUTS[Math.min(beatIndex, LAYOUTS.length - 1)];

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "#000",
        color: `rgb(${CREAM_RGB})`,
        fontFamily:
          "'IBM Plex Mono', ui-monospace, SFMono-Regular, Menlo, monospace",
        overflow: "hidden",
      }}
    >
      <ChapterKeyframes />
      <BlueprintGrid />

      {/*
        Camera center at 0.54 puts scene subjects around the golden
        mean (38% from top). Text sits at 62% from top — its mirror
        across the mean. Eye moves minimally between them.
      */}
      <Chapter1Engine
        layout={layout}
        beatTime={beatTime}
        cameraCenterRatio={0.54}
        audioEngine={audioRef.current}
      />

      <TextBackdropGradient />
      {beat.text && (
        <TextLayer
          beatId={beat.id}
          text={beat.text}
          // Finale beat — whispered "beyond... the noise..." at
          // speed 0.68 with an ellipsis pause runs ~4.2s. Text holds
          // ~5.0s so the last word can fully decay into reverb
          // silence before the subtitle leaves frame, then the
          // wordmark rises underneath.
          duration={
            beat.id === 45
              ? 5000
              : Math.max(MIN_BEAT_MS, beat.duration)
          }
        />
      )}
      {beat.id === 45 && <BrandReveal beatTime={beatTime} />}

      <Chrome
        beatIndex={beatIndex}
        totalBeats={BEATS.length}
        paused={paused}
        muted={muted}
        onTogglePause={() => setPaused((p) => !p)}
        onToggleMute={handleToggleMute}
        onRestart={() => {
          audioRef.current?.fadeMusicTo(0.28, 0.6);
          setBeatIndex(0);
          setBeatStart(performance.now());
          setBeatTime(0);
        }}
        onStep={(d) =>
          setBeatIndex((i) => Math.max(0, Math.min(BEATS.length - 1, i + d)))
        }
      />

      {!started && <TapToStart onStart={handleStart} />}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Gradient underlay for the text band. Keeps text readable without a
// hard horizontal line.
// ──────────────────────────────────────────────────────────────────────

function TextBackdropGradient() {
  return (
    <div
      aria-hidden
      style={{
        position: "absolute",
        left: 0,
        right: 0,
        bottom: 0,
        height: "48vh",
        background:
          "linear-gradient(to bottom, rgba(0,0,0,0) 0%, rgba(0,0,0,0.3) 40%, rgba(0,0,0,0.72) 100%)",
        zIndex: 8,
        pointerEvents: "none",
      }}
    />
  );
}

// ──────────────────────────────────────────────────────────────────────
// Text — lower third, smaller size, fades in and out
// ──────────────────────────────────────────────────────────────────────

function TextLayer({
  beatId,
  text,
  duration,
}: {
  beatId: number;
  text: string;
  duration: number;
}) {
  const [fadingOut, setFadingOut] = useState(false);
  useEffect(() => {
    setFadingOut(false);
    const fadeStart = Math.max(0, duration - TEXT_FADE_OUT_MS);
    const id = window.setTimeout(() => setFadingOut(true), fadeStart);
    return () => window.clearTimeout(id);
  }, [beatId, duration]);

  return (
    <section
      style={{
        position: "absolute",
        left: 0,
        right: 0,
        // Lower-third placement — far enough below the scene that
        // the text reads as a subtitle, not a competing focal point.
        // Eye rests on the animation; glances down for context only.
        bottom: "12%",
        top: "auto",
        height: "14vh",
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
        padding: "0 clamp(40px, 7vw, 112px)",
        zIndex: 10,
        pointerEvents: "none",
      }}
    >
      <h2
        key={beatId}
        style={{
          fontSize: "clamp(15px, 1.7vw, 20px)",
          fontWeight: 400,
          letterSpacing: -0.1,
          lineHeight: 1.45,
          color: `rgba(${CREAM_RGB}, 0.82)`,
          textAlign: "center",
          margin: 0,
          maxWidth: 640,
          opacity: fadingOut ? 0 : 1,
          transform: fadingOut ? "translateY(-4px)" : "translateY(0)",
          filter: fadingOut ? "blur(3px)" : "blur(0)",
          transition: fadingOut
            ? `opacity ${TEXT_FADE_OUT_MS}ms ease, transform ${TEXT_FADE_OUT_MS}ms ease, filter ${TEXT_FADE_OUT_MS}ms ease`
            : "none",
          animation: fadingOut
            ? undefined
            : "chapter-text-in 320ms cubic-bezier(0.16, 1, 0.3, 1) both",
        }}
      >
        {text}
      </h2>
    </section>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Chrome
// ──────────────────────────────────────────────────────────────────────

function Chrome({
  paused,
  muted,
  onTogglePause,
  onToggleMute,
  onRestart,
  onStep,
}: {
  beatIndex: number;
  totalBeats: number;
  paused: boolean;
  muted: boolean;
  onTogglePause: () => void;
  onToggleMute: () => void;
  onRestart: () => void;
  onStep: (delta: number) => void;
}) {
  return (
    <footer
      style={{
        position: "absolute",
        bottom: 0,
        left: 0,
        right: 0,
        padding: "clamp(12px, 2.4vw, 28px) clamp(16px, 4vw, 56px)",
        display: "flex",
        flexWrap: "wrap",
        justifyContent: "space-between",
        alignItems: "center",
        gap: "10px",
        zIndex: 20,
        ...LABEL.small,
        color: cream(ALPHA.labelDim),
      }}
    >
      <span style={{ letterSpacing: 4, userSelect: "none" }}>
        © {new Date().getFullYear()} suron
      </span>
      <div style={{ display: "flex", gap: 6 }}>
        <ChromeButton
          onClick={onToggleMute}
          label={muted ? "unmute" : "mute"}
          icon={muted ? <IconMuted /> : <IconSound />}
        />
        <ChromeButton onClick={() => onStep(-1)} label="prev" icon={<IconPrev />} />
        <ChromeButton
          onClick={onTogglePause}
          label={paused ? "play" : "pause"}
          icon={paused ? <IconPlay /> : <IconPause />}
        />
        <ChromeButton onClick={() => onStep(1)} label="next" icon={<IconNext />} />
        <ChromeButton onClick={onRestart} label="restart" icon={<IconRestart />} />
      </div>
    </footer>
  );
}

function ChromeButton({
  onClick,
  label,
  icon,
}: {
  onClick: () => void;
  label: string;
  icon: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      title={label}
      style={{
        background: "rgba(0, 0, 0, 0.4)",
        border: `1px solid ${cream(ALPHA.lineDim)}`,
        color: cream(ALPHA.labelActive),
        // Visual size stays compact on desktop; bumps to ≥36 px on
        // narrow viewports so it meets the mobile touch-target minimum.
        width: "clamp(30px, 8vw, 36px)",
        height: "clamp(30px, 8vw, 36px)",
        padding: 0,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: "pointer",
        touchAction: "manipulation",
        WebkitTapHighlightColor: "transparent",
      }}
    >
      {icon}
    </button>
  );
}

function IconPrev() {
  return (
    <svg width="10" height="10" viewBox="0 0 10 10" fill="currentColor" aria-hidden>
      <rect x="1" y="1" width="1.5" height="8" />
      <path d="M9 1 L9 9 L3.5 5 Z" />
    </svg>
  );
}
function IconNext() {
  return (
    <svg width="10" height="10" viewBox="0 0 10 10" fill="currentColor" aria-hidden>
      <rect x="7.5" y="1" width="1.5" height="8" />
      <path d="M1 1 L1 9 L6.5 5 Z" />
    </svg>
  );
}
function IconPlay() {
  return (
    <svg width="10" height="10" viewBox="0 0 10 10" fill="currentColor" aria-hidden>
      <path d="M2 1 L2 9 L9 5 Z" />
    </svg>
  );
}
function IconPause() {
  return (
    <svg width="10" height="10" viewBox="0 0 10 10" fill="currentColor" aria-hidden>
      <rect x="2" y="1" width="2" height="8" />
      <rect x="6" y="1" width="2" height="8" />
    </svg>
  );
}
function IconRestart() {
  return (
    <svg width="11" height="11" viewBox="0 0 11 11" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M9 5.5 A3.5 3.5 0 1 1 5.5 2" />
      <path d="M5.5 0.5 L5.5 3 L3 3" />
    </svg>
  );
}
function IconSound() {
  return (
    <svg width="11" height="11" viewBox="0 0 11 11" fill="none" stroke="currentColor" strokeWidth="1.1" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M1.5 4 L3.5 4 L5.5 2 L5.5 9 L3.5 7 L1.5 7 Z" fill="currentColor" />
      <path d="M7.2 3.5 A2.8 2.8 0 0 1 7.2 7.5" />
      <path d="M8.6 2 A4.8 4.8 0 0 1 8.6 9" opacity="0.6" />
    </svg>
  );
}
function IconMuted() {
  return (
    <svg width="11" height="11" viewBox="0 0 11 11" fill="none" stroke="currentColor" strokeWidth="1.1" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M1.5 4 L3.5 4 L5.5 2 L5.5 9 L3.5 7 L1.5 7 Z" fill="currentColor" />
      <path d="M7.5 4 L10 6.5 M10 4 L7.5 6.5" />
    </svg>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Keyframes / font
// ──────────────────────────────────────────────────────────────────────

function ChapterKeyframes(): ReactNode {
  const css: CSSProperties = {};
  void css;
  return (
    <style>{`
      @keyframes chapter-text-in {
        from {
          opacity: 0;
          transform: translateY(4px);
          filter: blur(2px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
          filter: blur(0);
        }
      }
      html, body {
        margin: 0;
        background: #000 !important;
        overflow: hidden;
      }
    `}</style>
  );
}

function useIbmPlexMono(): void {
  useEffect(() => {
    const existing = document.querySelector(`link[href="${FONT_HREF}"]`);
    if (existing) return;
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = FONT_HREF;
    document.head.appendChild(link);
  }, []);
}
